import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Share2, Lock, Globe, Users, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAuthStore } from '@/stores/authStore';
import {
  buildShareLink,
  loadWorkflowShares,
  saveWorkflowShares,
  type WorkflowShareRecord,
} from '@/lib/workflowShares';

export function WorkflowCollaboration() {
  const workflows = useWorkflowStore((s) => s.workflows);
  const email = useAuthStore((s) => s.email);
  const [sharedWorkflows, setSharedWorkflows] = useState<WorkflowShareRecord[]>(() => loadWorkflowShares());

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPublicDialog, setShowPublicDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'view' | 'edit' | 'admin'>('view');
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);

  useEffect(() => {
    saveWorkflowShares(sharedWorkflows);
  }, [sharedWorkflows]);

  const teamMembers = [
    { id: 'owner', name: 'You', email: email ?? 'signed-in user', role: 'owner' as const },
  ];

  const persistShares = (next: WorkflowShareRecord[]) => {
    setSharedWorkflows(next);
    saveWorkflowShares(next);
  };

  const handleShareWorkflow = () => {
    if (!selectedWorkflow || !shareEmail) {
      toast.error('Select workflow and email');
      return;
    }

    const workflow = workflows.find((w) => w.id === selectedWorkflow);
    if (!workflow) {
      toast.error('Workflow not found');
      return;
    }

    const existing = sharedWorkflows.find((s) => s.workflowId === workflow.id);
    if (existing?.sharedWith.includes(shareEmail)) {
      toast.error('Already shared with this email');
      return;
    }

    if (existing) {
      persistShares(
        sharedWorkflows.map((s) =>
          s.workflowId === workflow.id
            ? { ...s, sharedWith: [...s.sharedWith, shareEmail], permission: sharePermission }
            : s
        )
      );
    } else {
      persistShares([
        ...sharedWorkflows,
        {
          id: Math.random().toString(36).slice(2, 11),
          workflowId: workflow.id,
          workflowName: workflow.name,
          sharedWith: [shareEmail],
          permission: sharePermission,
          sharedAt: new Date().toISOString(),
          link: buildShareLink(workflow.id),
        },
      ]);
    }

    setShareEmail('');
    setSelectedWorkflow('');
    setShowShareDialog(false);
    toast.success(`Shared with ${shareEmail}`);
  };

  const handleGenerateLink = (workflowId: string) => {
    const link = buildShareLink(workflowId);
    const updated = sharedWorkflows.some((s) => s.workflowId === workflowId)
      ? sharedWorkflows.map((s) => (s.workflowId === workflowId ? { ...s, link } : s))
      : [
          ...sharedWorkflows,
          {
            id: Math.random().toString(36).slice(2, 11),
            workflowId,
            workflowName: workflows.find((w) => w.id === workflowId)?.name ?? 'Flow',
            sharedWith: [],
            permission: 'view' as const,
            sharedAt: new Date().toISOString(),
            link,
          },
        ];
    persistShares(updated);
    toast.success('Share link copied to clipboard');
    void navigator.clipboard.writeText(link);
  };

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Link copied to clipboard');
    });
  };

  const handleRemoveShare = (workflowId: string, email: string) => {
    const updated = sharedWorkflows.map((w) =>
      w.workflowId === workflowId
        ? { ...w, sharedWith: w.sharedWith.filter((e) => e !== email) }
        : w
    );
    persistShares(updated);
    toast.success(`Removed ${email}'s access`);
  };

  const handleDeleteShare = (workflowId: string) => {
    persistShares(sharedWorkflows.filter((w) => w.workflowId !== workflowId));
    setShowDeleteDialog(null);
    toast.success('Share deleted');
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-500/10 text-purple-700 border-purple-500/20';
      case 'editor':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'viewer':
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
      default:
        return 'bg-muted';
    }
  };

  const getPermissionColor = (permission: string) => {
    switch (permission) {
      case 'admin':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'edit':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'view':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground mb-2">Collaboration</h2>
          <p className="text-muted-foreground">Share workflows and manage team access</p>
        </div>
        <Button onClick={() => setShowShareDialog(true)}>
          <Share2 className="w-4 h-4 mr-2" />
          Share Workflow
        </Button>
      </div>

      {/* Team Section */}
      <Card className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Users className="w-5 h-5" />
          Team Members
        </h3>

        <div className="grid gap-3">
          {teamMembers.map((member) => (
            <div key={member.id} className="flex items-center justify-between p-3 rounded border border-border">
              <div className="flex-1">
                <p className="font-medium text-foreground">{member.name}</p>
                <p className="text-sm text-muted-foreground">{member.email}</p>
              </div>
              <Badge className={getRoleColor(member.role)}>
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </Badge>
            </div>
          ))}
        </div>

        <Button variant="outline" className="w-full">
          <Mail className="w-4 h-4 mr-2" />
          Invite Team Member
        </Button>
      </Card>

      {/* Shared Workflows */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Shared Workflows</h3>

        {sharedWorkflows.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            <p className="text-sm mb-4">No workflows shared yet.</p>
            <Button onClick={() => setShowShareDialog(true)}>Share Your First Workflow</Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {sharedWorkflows.map((workflow) => (
              <Card key={workflow.id} className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{workflow.workflowName}</h4>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Badge className={getPermissionColor(workflow.permission)}>
                        {workflow.permission === 'view' ? 'View Only' : workflow.permission === 'edit' ? 'Can Edit' : 'Admin'}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        Shared {new Date(workflow.sharedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedWorkflow(workflow.workflowId);
                        setShowPublicDialog(true);
                      }}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Public Link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setShowDeleteDialog(workflow.workflowId)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Shared Users */}
                {workflow.sharedWith.length > 0 && (
                  <div className="pt-4 border-t border-border space-y-2">
                    <p className="text-sm font-medium text-foreground">Shared with:</p>
                    <div className="flex flex-wrap gap-2">
                      {workflow.sharedWith.map((email) => (
                        <div
                          key={email}
                          className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-sm"
                        >
                          <span className="text-foreground">{email}</span>
                          <button
                            onClick={() => handleRemoveShare(workflow.workflowId, email)}
                            className="text-muted-foreground hover:text-foreground ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Public Link */}
                {workflow.link && (
                  <div className="pt-4 border-t border-border space-y-2">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm font-medium text-foreground">Public Link</p>
                    </div>
                    <div className="flex gap-2 p-3 rounded bg-muted/50 border border-border">
                      <input
                        type="text"
                        value={workflow.link}
                        readOnly
                        className="flex-1 bg-transparent text-sm text-muted-foreground outline-none"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyLink(workflow.link!)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Workflow</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Workflow</label>
              <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose workflow..." />
                </SelectTrigger>
                <SelectContent>
                  {sharedWorkflows.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.workflowName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Email Address</label>
              <Input
                placeholder="colleague@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Permission Level</label>
              <Select value={sharePermission} onValueChange={(v: any) => setSharePermission(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View Only</SelectItem>
                  <SelectItem value="edit">Can Edit</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Card className="p-3 bg-blue-500/5 border-blue-500/20">
              <p className="text-sm text-muted-foreground">
                They will receive an email invitation to access this workflow with the selected permissions.
              </p>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleShareWorkflow}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public Link Dialog */}
      <Dialog open={showPublicDialog} onOpenChange={setShowPublicDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Public Link Settings</DialogTitle>
          </DialogHeader>

          {selectedWorkflow && sharedWorkflows.find((w) => w.workflowId === selectedWorkflow) && (
            <div className="space-y-4 py-4">
              {sharedWorkflows.find((w) => w.workflowId === selectedWorkflow)?.link ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Public link is active</p>
                  <div className="flex gap-2 p-3 rounded bg-muted/50 border border-border">
                    <input
                      type="text"
                      value={sharedWorkflows.find((w) => w.workflowId === selectedWorkflow)?.link || ''}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-muted-foreground outline-none"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleCopyLink(
                          sharedWorkflows.find((w) => w.workflowId === selectedWorkflow)?.link || ''
                        )
                      }
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Generate a public link to share this workflow with anyone
                  </p>
                  <Button
                    className="w-full"
                    onClick={() => selectedWorkflow && handleGenerateLink(selectedWorkflow)}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Generate Public Link
                  </Button>
                </div>
              )}

              <Card className="p-3 bg-amber-500/5 border-amber-500/20 flex gap-2">
                <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Anyone with this link can view the workflow. Disable it anytime.
                </p>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublicDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog !== null} onOpenChange={(open) => !open && setShowDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Share</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to stop sharing this workflow? People who have access will lose
              access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showDeleteDialog && handleDeleteShare(showDeleteDialog)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tips */}
      <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-blue-500/5 border-blue-500/20">
        <h3 className="font-semibold text-foreground mb-3">Collaboration Tips</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Use View Only permission for sharing workflows without edit access</li>
          <li>• Admin permissions allow others to manage sharing and permissions</li>
          <li>• Public links are useful for quick sharing without email invites</li>
          <li>• Monitor who has access to sensitive workflows</li>
          <li>• Remove access immediately when team members leave</li>
        </ul>
      </Card>
    </div>
  );
}
